import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Application } from '@/types';
import { toast } from 'sonner';

export function ApplicationsPage() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .order('created_at', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        toast.error(`Applications fetch error: ${error.message}`);
        console.error('Applications fetch error:', error);
        return;
      }

      console.log('Fetched applications:', data);

      if (!data || data.length === 0) {
        toast.info('No applications found for your company');
      }

      setApplications(data || []);
    } catch (error) {
      console.error('Unexpected error fetching applications:', error);
      toast.error('An unexpected error occurred while fetching applications');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'reviewed':
        return 'bg-blue-500';
      case 'shortlisted':
        return 'bg-green-500';
      case 'rejected':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Job Applications</h1>
      {loading ? (
        <div className="text-center py-10">Loading applications...</div>
      ) : applications.length === 0 ? (
        <div className="text-center py-10">No applications found</div>
      ) : (
        <div className="space-y-8">
          {Object.entries(
            applications.reduce((acc, application) => {
              const companyName = application.company_name || 'Unknown Company';
              return {
                ...acc,
                [companyName]: [...(acc[companyName] || []), application],
              };
            }, {} as Record<string, Application[]>)
          ).map(([companyName, companyApplications]) => (
            <div key={companyName} className="space-y-4">
              <div className="border-b pb-2">
                <h2 className="text-2xl font-semibold">{companyName}</h2>
                <p className="text-sm text-muted-foreground">
                  {companyApplications.length} application{companyApplications.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {companyApplications.map((application) => (
                  <div key={application.id} className="border rounded-lg p-4 space-y-2 bg-card">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold">{application.application_data.job_title || 'No Title'}</h3>
                      <span className={`px-2 py-1 rounded-full text-white text-sm ${getStatusColor(application.status)}`}>
                        {application.status}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Candidate: {application.application_data.candidate_name || 'Unknown'}</p>
                      <p className="text-sm text-muted-foreground">Applied on: {new Date(application.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}